import React, { useState } from "react";
import { Checkbox, Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useConfluenceSourceSettingsText,
  useConfluenceSourceSettingsViewModel,
} from "@onetasklist/shared/sources/confluence";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface ConfluenceSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const ConfluenceSourceSettings: React.FC<
  ConfluenceSourceSettingsProps
> = ({ editingConnection, onUpdate, sectionOffset }) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useConfluenceSourceSettingsText(t);
  const vm = useConfluenceSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [resourceQuery, setResourceQuery] = useState("");
  const [spacesQuery, setSpacesQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <SettingsSectionCard
      sectionOffset={sectionOffset}
      title={t(
        "board.connections.catalogue.sourceSettings.confluence.sectionSpaces",
      )}
    >
      {vm.resources.length > 1 && (
        <Field orientation="horizontal" label={text.resourceLabelText}>
          <SourceSingleTagPicker
            selectedId={
              vm.selectedResourceId === vm.noneOptionValue
                ? undefined
                : vm.selectedResourceId
            }
            selectedLabel={vm.selectedResourceName}
            query={resourceQuery}
            placeholder={text.resourcePlaceholderText}
            options={vm.resources.map((resource) => ({
              id: resource.id,
              label: resource.name || resource.id,
            }))}
            onQueryChange={setResourceQuery}
            onSelect={vm.onSelectResource}
            clearSelectionText={text.resourcePlaceholderText}
            isLoading={!vm.resourcesLoaded}
          />
        </Field>
      )}

      {vm.selectedResourceId !== vm.noneOptionValue && (
        <Field
          orientation="horizontal"
          label={text.spacesLabelText}
          hint={text.spacesHintText}
        >
          <SourceMultiTagPicker
            selectedIds={vm.selectedSpaceIds}
            query={spacesQuery}
            placeholder={text.spacesPlaceholderText}
            options={vm.spaces.map((space) => ({
              id: space.id,
              label: space.name || space.id,
            }))}
            onQueryChange={setSpacesQuery}
            onSelectionChange={vm.onSelectSpaces}
            minQueryLength={0}
            typeToSearchText={text.spacesHintText}
            isLoading={!vm.spacesLoaded}
          />
        </Field>
      )}

      <Field orientation="horizontal" label={text.assignedToMeLabelText}>
        <Checkbox
          checked={vm.assignedToMe}
          onChange={(_, data) => vm.onToggleAssignedToMe(Boolean(data.checked))}
        />
      </Field>

      {!vm.resourcesLoaded && <Text>{text.loadingResourcesText}</Text>}
      {vm.resourcesLoaded &&
        !vm.spacesLoaded &&
        vm.selectedResourceId !== vm.noneOptionValue && (
          <Text>{text.loadingSpacesText}</Text>
        )}
      {vm.hasError && <Text>{text.loadErrorText}</Text>}
    </SettingsSectionCard>
  );
};
